import { useState, useEffect } from "react";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Save,
  Building2,
  MapPin,
  Phone,
  Mail,
  Image,
  Percent,
  DollarSign,
  Shield,
  FileText,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { CURRENCIES } from "@/utils/currencies";
import { showConfirm } from "@/utils/dialog";

const PRICING_VIEW_OPTIONS = [
  { value: "sell_only", label: "Sell price only (client invoice)" },
  { value: "cost_and_sell", label: "Cost + sell price (internal/proforma)" },
  { value: "margin", label: "Show margin / markup %" },
  { value: "hide_prices", label: "Hide prices (quotation style)" },
];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isFinanceOrAdmin = user?.role === "finance" || user?.role === "admin";
  const [settings, setSettings] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    logo_url: "",
    vat_rate: 5.0,
    currency: "OMR",
    bank_details: null,
    invoice_notes: "",
    invoice_approval_policy: "any_one",
  });
  const [defaultPricingView, setDefaultPricingView] = useState({ mode: "sell_only", show_markup: false });
  const [dailyReport, setDailyReport] = useState({
    enabled: false,
    report_time: "19:00",
    email: "",
    smtp_host: "",
    smtp_port: 587,
    smtp_secure: false,
    smtp_user: "",
    smtp_pass: "",
    smtp_from: "",
    smtp_password_set: false,
  });
  const [savingDailyReport, setSavingDailyReport] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [savingPricingView, setSavingPricingView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [serviceTypes, setServiceTypes] = useState([]);
  const [minPrices, setMinPrices] = useState({});
  const [savingMinPrices, setSavingMinPrices] = useState(false);
  const [invoiceTemplate, setInvoiceTemplate] = useState({
    primary_color: "#0891b2",
    show_logo: true,
    footer_text: "",
    table_header_bg: "#e5e7eb",
    table_header_text: "#0891b2",
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [invoiceNumberFormat, setInvoiceNumberFormat] = useState({
    prefix: "CAN",
    use_year: true,
    random_type: "digits",
    random_length: 4,
    postfix: "",
  });
  const [savingInvoiceNumberFormat, setSavingInvoiceNumberFormat] = useState(false);
  const [dangerForm, setDangerForm] = useState({
    admin_password: "",
    confirmation_text: "",
  });
  const [clearingData, setClearingData] = useState(false);
  const [resetPreview, setResetPreview] = useState(null);
  const [loadingResetPreview, setLoadingResetPreview] = useState(false);
  const [roleAccessMatrix, setRoleAccessMatrix] = useState(null);
  const [loadingRoleAccess, setLoadingRoleAccess] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      api.settings.getInvoiceTemplate().then(setInvoiceTemplate).catch(() => {});
      api.settings.getInvoiceNumberFormat().then(setInvoiceNumberFormat).catch(() => {});
      api.settings.getDailyReport().then((data) => {
        setDailyReport({
          enabled: Boolean(data?.enabled),
          report_time: data?.report_time || "19:00",
          email: data?.email || "",
          smtp_host: data?.smtp_host || "",
          smtp_port: data?.smtp_port || 587,
          smtp_secure: Boolean(data?.smtp_secure),
          smtp_user: data?.smtp_user || "",
          smtp_pass: "",
          smtp_from: data?.smtp_from || "",
          smtp_password_set: Boolean(data?.smtp_password_set),
        });
        setTestEmailTo(data?.email || user?.email || "");
      }).catch(() => {});
    }
  }, [isAdmin, user?.email]);

  useEffect(() => {
    if (isAdmin) {
      loadResetPreview();
    }
  }, [isAdmin]);

  const loadResetPreview = async () => {
    setLoadingResetPreview(true);
    try {
      const data = await api.settings.getResetCompanyDataPreview();
      setResetPreview(data || null);
    } catch (_) {
      setResetPreview(null);
    } finally {
      setLoadingResetPreview(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      api.orders.getServiceTypes().then((r) => setServiceTypes(r.service_types || []));
      api.settings.getMinPrices().then((rows) => {
        const map = {};
        (rows || []).forEach((row) => (map[row.service_type] = row.min_amount));
        setMinPrices(map);
      }).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isFinanceOrAdmin) {
      api.settings.getDefaultPricingView().then((pv) => setDefaultPricingView(pv || { mode: "sell_only", show_markup: false })).catch(() => {});
    }
  }, [isFinanceOrAdmin]);

  useEffect(() => {
    setLoadingRoleAccess(true);
    api.settings
      .getRoleAccessMatrix()
      .then((data) => setRoleAccessMatrix(data || null))
      .catch(() => setRoleAccessMatrix(null))
      .finally(() => setLoadingRoleAccess(false));
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api.settings.getCompany();
      let bankDetails = data.bank_details;
      if (typeof bankDetails === "string") {
        try {
          bankDetails = bankDetails ? JSON.parse(bankDetails) : null;
        } catch (_) {
          bankDetails = null;
        }
      }
      setSettings({
        name: data.name || "",
        address: data.address || "",
        phone: data.phone || "",
        email: data.email || "",
        logo_url: data.logo_url || "",
        vat_rate: data.vat_rate || 5.0,
        currency: data.currency || "OMR",
        bank_details: bankDetails || null,
        invoice_notes: data.invoice_notes || "",
        invoice_approval_policy: data.invoice_approval_policy || "any_one",
      });
    } catch (error) {
      console.error("Failed to load settings:", error);
      setMessage({ type: "error", text: "Failed to load settings" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const updated = await api.settings.updateCompany(settings);
      let bankDetails = updated.bank_details;
      if (typeof bankDetails === "string") {
        try {
          bankDetails = bankDetails ? JSON.parse(bankDetails) : null;
        } catch (_) {
          bankDetails = null;
        }
      }
      setSettings({
        name: updated.name || "",
        address: updated.address || "",
        phone: updated.phone || "",
        email: updated.email || "",
        logo_url: updated.logo_url || "",
        vat_rate: updated.vat_rate || 5.0,
        currency: updated.currency || "OMR",
        bank_details: bankDetails || null,
        invoice_notes: updated.invoice_notes || "",
        invoice_approval_policy: updated.invoice_approval_policy || "any_one",
      });
      setMessage({
        type: "success",
        text: "Company settings saved successfully!",
      });
    } catch (error) {
      console.error("Failed to save settings:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to save settings",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
    setMessage({ type: "", text: "" });
  };

  const handleBankDetailChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      bank_details: {
        ...(prev.bank_details || {}),
        [field]: value,
      },
    }));
    setMessage({ type: "", text: "" });
  };

  const handleMinPriceChange = (serviceType, value) => {
    const v = value === "" ? null : parseFloat(value);
    setMinPrices((prev) => ({
      ...prev,
      [serviceType]: v,
    }));
  };

  const handleSaveMinPrices = async () => {
    setSavingMinPrices(true);
    setMessage({ type: "", text: "" });
    try {
      const prices = Object.entries(minPrices)
        .filter(([, amount]) => amount != null && !isNaN(amount) && amount >= 0)
        .map(([service_type, min_amount]) => ({ service_type, min_amount }));
      await api.settings.updateMinPrices(prices);
      setMessage({ type: "success", text: "Minimum prices saved. Orders below these amounts cannot be closed." });
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Failed to save minimum prices" });
    } finally {
      setSavingMinPrices(false);
    }
  };


  const handleResetCompanyData = async () => {
    setMessage({ type: "", text: "" });
    if (!dangerForm.admin_password) {
      setMessage({ type: "error", text: "Admin password is required for data reset." });
      return;
    }
    if ((dangerForm.confirmation_text || "").trim() !== "DELETE MY DATA") {
      setMessage({ type: "error", text: 'Type "DELETE MY DATA" exactly to confirm.' });
      return;
    }
    const acknowledged = await showConfirm(
      "This will permanently delete all transactional data for this company. Users and subscription stay intact. Continue?",
      "Danger Zone",
      "Continue",
    );
    if (!acknowledged) return;

    setClearingData(true);
    try {
      const result = await api.settings.resetCompanyData(dangerForm);
      setDangerForm({ admin_password: "", confirmation_text: "" });
      await loadResetPreview();
      setMessage({
        type: "success",
        text: result?.message || "Company data cleared successfully.",
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "Failed to clear company data.",
      });
    } finally {
      setClearingData(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0 max-w-full">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-heading font-semibold text-secondary">
          Company Settings
        </h1>
      </div>

      {message.text && (
        <div
          className={`p-4 rounded-lg ${
            message.type === "success"
              ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
              : "bg-red-50 border border-red-200 text-red-700"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-stone-900 mb-2 flex items-center gap-2">
          <Shield size={20} className="text-cyan-600" />
          Role access matrix
        </h2>
        <p className="form-helper mb-4 max-w-full">
          Live visibility of role access so team knows who can do what.
        </p>
        {loadingRoleAccess ? (
          <p className="text-sm text-stone-500">Loading role access...</p>
        ) : !roleAccessMatrix?.roles ? (
          <p className="text-sm text-red-600">Role access matrix unavailable.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {["admin", "finance", "management", "sales"].map((roleKey) => {
              const role = roleAccessMatrix.roles?.[roleKey];
              if (!role) return null;
              const moduleEntries = Object.entries(role.modules || {});
              return (
                <div
                  key={roleKey}
                  className="rounded-lg border border-stone-200 p-4 bg-stone-50/50"
                >
                  <h3 className="font-semibold text-stone-800 mb-2">
                    {role.label}
                  </h3>
                  <p className="text-sm text-stone-600 mb-3">
                    {role.description || "-"}
                  </p>
                  <p className="text-xs uppercase tracking-wide text-stone-500 mb-1">
                    Module Permissions
                  </p>
                  <div className="space-y-1.5">
                    {moduleEntries.map(([moduleName, actions]) => (
                      <p key={moduleName} className="text-sm text-stone-700">
                        <span className="font-medium capitalize">
                          {moduleName.replace(/_/g, " ")}:
                        </span>{" "}
                        {Array.isArray(actions) ? actions.join(", ") : "-"}
                      </p>
                    ))}
                    {!moduleEntries.length && (
                      <p className="text-sm text-stone-500">No module access mapped.</p>
                    )}
                  </div>
                  <p className="text-xs uppercase tracking-wide text-stone-500 mt-3 mb-1">
                    Reports Access
                  </p>
                  <p className="text-sm text-stone-700">
                    {role.report_access?.enabled ? "Enabled" : "Not enabled"}
                  </p>
                  <p className="text-xs text-stone-600 mt-1">
                    Why: {role.report_access?.reason || "-"}
                  </p>
                  {role.report_access?.enabled && Array.isArray(role.report_access?.reports) && role.report_access.reports.length > 0 && (
                    <p className="text-xs text-stone-600 mt-1">
                      Reports: {role.report_access.reports.join(" • ")}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Building2 size={20} className="text-cyan-600" />
            Company Information
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                value={settings.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
                className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                placeholder="Your Company Name"
              />
              <p className="form-helper">This will appear on all your invoices</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                <MapPin size={16} />
                Address
              </label>
              <textarea
                value={settings.address}
                onChange={(e) => handleChange("address", e.target.value)}
                rows={3}
                className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                placeholder="Street Address, City, Country"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                  <Phone size={16} />
                  Phone
                </label>
                <input
                  type="tel"
                  value={settings.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                  placeholder="+968 1234 5678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                  <Mail size={16} />
                  Email
                </label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                  placeholder="info@yourcompany.com"
                />
              </div>
            </div>

            <div className="form-field-wrap">
              <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                <Image size={16} />
                Logo URL
              </label>
              <input
                type="url"
                value={settings.logo_url}
                onChange={(e) => handleChange("logo_url", e.target.value)}
                className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                placeholder="https://example.com/logo.png"
              />
              <p className="form-helper">URL to your company logo (will appear on invoice PDFs). Use <strong>PNG or JPEG</strong> only — SVG is not supported and will not show in the PDF.</p>
              {settings.logo_url && settings.logo_url.trim() && (
                <div className="mt-3 p-3 bg-stone-50 rounded-lg border border-stone-200 max-w-md">
                  <p className="text-xs text-stone-600 mb-2">Preview (width 100%, height auto):</p>
                  <img
                    src={settings.logo_url.trim()}
                    alt="Company logo preview"
                    className="w-full h-auto object-contain max-h-32"
                    style={{ width: "100%", height: "auto" }}
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Percent size={20} className="text-cyan-600" />
            Tax & Currency Settings
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Default VAT Rate (%)
              </label>
              <input
                type="number"
                value={settings.vat_rate}
                onChange={(e) =>
                    handleChange(
                      "vat_rate",
                      e.target.value === "" ? "" : parseFloat(e.target.value),
                    )
                }
                min="0"
                max="100"
                step="0.01"
                className="w-full md:w-48 border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                placeholder="5.00"
              />
              <p className="form-helper">Default VAT percentage for new invoices (can be changed per invoice).</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                <DollarSign size={16} />
                Default Currency
              </label>
              <select
                value={settings.currency}
                onChange={(e) => handleChange("currency", e.target.value)}
                className="w-full md:w-64 border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name} ({currency.symbol})
                  </option>
                ))}
              </select>
              <p className="form-helper">Default currency for invoices and forms (can be changed per invoice).</p>
            </div>
          </div>
        </div>

        {isFinanceOrAdmin && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <FileText size={20} className="text-cyan-600" />
              Invoice notes
            </h2>
            <p className="form-helper mb-4 max-w-full">This text appears in the Notes section on the invoice view and PDF. It comes from here (settings), not from service or order notes. Leave empty to show no notes on invoices.</p>
            <textarea
              value={settings.invoice_notes || ""}
              onChange={(e) => handleChange("invoice_notes", e.target.value)}
              rows={4}
              placeholder="e.g. Payment terms, thank you message..."
              className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600 resize-y"
            />
            {isAdmin && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Invoice approval policy
                </label>
                <select
                  value={settings.invoice_approval_policy || "any_one"}
                  onChange={(e) =>
                    handleChange("invoice_approval_policy", e.target.value)
                  }
                  className="w-full md:w-80 border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                >
                  <option value="none">None (no pre-conversion approval required)</option>
                  <option value="any_one">Any one (manager or accountant)</option>
                  <option value="both">Both manager and accountant</option>
                </select>
                <p className="form-helper mt-1">
                  Controls required approvals before converting an order into an invoice.
                </p>
              </div>
            )}
          </div>
        )}

        {isAdmin && (
          <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-red-700 mb-4 flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-600" />
              Danger zone - delete all company data
            </h2>
            <p className="text-sm text-red-700 mb-4">
              Use this only for test reset. This permanently clears transactional
              records (invoices, payments, reports data, ledgers, orders,
              company/customer/vendor account entries). Team users, customers,
              vendors, login, and subscription stay intact.
            </p>
            <div className="mb-4 rounded-lg border border-red-100 bg-red-50/50 p-3 text-sm text-red-800">
              {loadingResetPreview ? (
                <p>Loading delete preview...</p>
              ) : resetPreview?.counts ? (
                <div className="space-y-1">
                  <p className="font-medium">
                    Will delete about {Number(resetPreview.total_records || 0)} records:
                  </p>
                  <p>
                    Invoices {resetPreview.counts.invoices || 0}, Payments {resetPreview.counts.payments || 0},
                    Ledger {resetPreview.counts.ledger_entries || 0}, Orders {resetPreview.counts.orders || 0},
                    Vendor payments {resetPreview.counts.vendor_payments || 0}, Opening balances {resetPreview.counts.opening_balances || 0}
                  </p>
                  <p>
                    Keeping master records: Customers {resetPreview.counts.customers_kept || 0}, Vendors {resetPreview.counts.vendors_kept || 0}
                  </p>
                </div>
              ) : (
                <p>Preview not available.</p>
              )}
              <button
                type="button"
                onClick={loadResetPreview}
                className="mt-2 text-xs underline underline-offset-2 hover:text-red-900"
              >
                Refresh preview
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Admin password
                </label>
                <input
                  type="password"
                  value={dangerForm.admin_password}
                  onChange={(e) =>
                    setDangerForm((prev) => ({
                      ...prev,
                      admin_password: e.target.value,
                    }))
                  }
                  className="w-full border border-stone-300 rounded-lg px-4 py-2"
                  placeholder="Enter admin password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Type confirmation text
                </label>
                <input
                  type="text"
                  value={dangerForm.confirmation_text}
                  onChange={(e) =>
                    setDangerForm((prev) => ({
                      ...prev,
                      confirmation_text: e.target.value,
                    }))
                  }
                  className="w-full border border-stone-300 rounded-lg px-4 py-2"
                  placeholder='Type exactly: DELETE MY DATA'
                />
              </div>
            </div>
            <button
              type="button"
              onClick={handleResetCompanyData}
              disabled={clearingData}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {clearingData ? "Deleting..." : "Delete all company data"}
            </button>
          </div>
        )}

        {isAdmin && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Clock size={20} className="text-cyan-600" />
              Daily report
            </h2>
            <p className="form-helper mb-4 max-w-full">
              Configure the daily dashboard/email report for staff performance, lead activity, and sales summary. SMTP settings are company-specific.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center gap-2 text-sm text-stone-700">
                <input
                  type="checkbox"
                  checked={dailyReport.enabled}
                  onChange={(e) =>
                    setDailyReport((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))
                  }
                />
                Enable auto email
              </label>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Report time
                </label>
                <input
                  type="time"
                  value={dailyReport.report_time}
                  onChange={(e) =>
                    setDailyReport((prev) => ({
                      ...prev,
                      report_time: e.target.value,
                    }))
                  }
                  className="w-full border border-stone-300 rounded-lg px-4 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Email ID
                </label>
                <input
                  type="email"
                  value={dailyReport.email}
                  onChange={(e) =>
                    setDailyReport((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  className="w-full border border-stone-300 rounded-lg px-4 py-2"
                />
              </div>
            </div>
            <div className="mt-4 border-t border-stone-200 pt-4">
              <h3 className="text-sm font-semibold text-stone-800 mb-3">
                Company Email Setup (SMTP)
              </h3>
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <p className="font-semibold mb-1">Gmail setup guide (important)</p>
                <p>
                  Gmail regular account password will not work here. You must use a
                  <strong> Google App Password</strong>.
                </p>
                <p className="mt-1">
                  Steps: Google Account, then Security, then enable 2-Step Verification, then open App passwords, then generate a Mail app password, then paste that value in SMTP Password.
                </p>
                <p className="mt-1">
                  Recommended Gmail SMTP: host <code>smtp.gmail.com</code>, port <code>587</code> (TLS, secure off) or <code>465</code> (SSL, secure on).
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    SMTP Host
                  </label>
                  <input
                    type="text"
                    value={dailyReport.smtp_host}
                    onChange={(e) =>
                      setDailyReport((prev) => ({
                        ...prev,
                        smtp_host: e.target.value,
                      }))
                    }
                    placeholder="smtp.gmail.com"
                    className="w-full border border-stone-300 rounded-lg px-4 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    SMTP Port
                  </label>
                  <input
                    type="number"
                    value={dailyReport.smtp_port}
                    onChange={(e) =>
                      setDailyReport((prev) => ({
                        ...prev,
                      smtp_port:
                        e.target.value === "" ? "" : Number(e.target.value),
                      }))
                    }
                    className="w-full border border-stone-300 rounded-lg px-4 py-2"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-stone-700 mt-7">
                  <input
                    type="checkbox"
                    checked={dailyReport.smtp_secure}
                    onChange={(e) =>
                      setDailyReport((prev) => ({
                        ...prev,
                        smtp_secure: e.target.checked,
                      }))
                    }
                  />
                  Use secure SMTP (SSL)
                </label>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    SMTP Username
                  </label>
                  <input
                    type="text"
                    value={dailyReport.smtp_user}
                    onChange={(e) =>
                      setDailyReport((prev) => ({
                        ...prev,
                        smtp_user: e.target.value,
                      }))
                    }
                    className="w-full border border-stone-300 rounded-lg px-4 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    SMTP Password
                  </label>
                  <input
                    type="password"
                    value={dailyReport.smtp_pass}
                    onChange={(e) =>
                      setDailyReport((prev) => ({
                        ...prev,
                        smtp_pass: e.target.value,
                      }))
                    }
                    placeholder={
                      dailyReport.smtp_password_set
                        ? "Saved. Leave blank to keep existing"
                        : "SMTP password or app password"
                    }
                    className="w-full border border-stone-300 rounded-lg px-4 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    From Email
                  </label>
                  <input
                    type="email"
                    value={dailyReport.smtp_from}
                    onChange={(e) =>
                      setDailyReport((prev) => ({
                        ...prev,
                        smtp_from: e.target.value,
                      }))
                    }
                    placeholder="reports@company.com"
                    className="w-full border border-stone-300 rounded-lg px-4 py-2"
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              disabled={savingDailyReport}
              onClick={async () => {
                setSavingDailyReport(true);
                setMessage({ type: "", text: "" });
                try {
                  const saved = await api.settings.updateDailyReport(dailyReport);
                  setDailyReport((prev) => ({
                    ...prev,
                    ...saved,
                    smtp_pass: "",
                    smtp_password_set: Boolean(saved?.smtp_password_set),
                  }));
                  setMessage({ type: "success", text: "Daily report settings saved." });
                } catch (err) {
                  setMessage({ type: "error", text: err.message || "Failed to save daily report settings" });
                } finally {
                  setSavingDailyReport(false);
                }
              }}
              className="mt-4 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {savingDailyReport ? "Saving..." : "Save daily report settings"}
            </button>
            <div className="mt-4 border-t border-stone-200 pt-4">
              <h4 className="text-sm font-semibold text-stone-800 mb-2">Test SMTP configuration</h4>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Send test email to
                  </label>
                  <input
                    type="email"
                    value={testEmailTo}
                    onChange={(e) => setTestEmailTo(e.target.value)}
                    placeholder="test@yourcompany.com"
                    className="w-full border border-stone-300 rounded-lg px-4 py-2"
                  />
                </div>
                <button
                  type="button"
                  disabled={sendingTestEmail}
                  onClick={async () => {
                    setSendingTestEmail(true);
                    setMessage({ type: "", text: "" });
                    try {
                      const result = await api.settings.sendDailyReportTestEmail({
                        to_email: testEmailTo,
                        smtp_host: dailyReport.smtp_host,
                        smtp_port: dailyReport.smtp_port,
                        smtp_secure: dailyReport.smtp_secure,
                        smtp_user: dailyReport.smtp_user,
                        smtp_pass: dailyReport.smtp_pass,
                        smtp_from: dailyReport.smtp_from,
                      });
                      setMessage({
                        type: "success",
                        text: result?.message || "Test email sent successfully.",
                      });
                    } catch (err) {
                      setMessage({
                        type: "error",
                        text: err.message || "Failed to send test email.",
                      });
                    } finally {
                      setSendingTestEmail(false);
                    }
                  }}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm disabled:opacity-50"
                >
                  {sendingTestEmail ? "Sending..." : "Send test email"}
                </button>
              </div>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <FileText size={20} className="text-cyan-600" />
              Bank details (for invoices)
            </h2>
            <p className="form-helper mb-4 max-w-full">Optional. If you fill any bank details below, they will appear on the invoice view and PDF. Leave all fields empty to hide bank details on invoices.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Bank Name</label>
                <input type="text" value={settings.bank_details?.bankName || ""} onChange={(e) => handleBankDetailChange("bankName", e.target.value)} placeholder="e.g. Bank Muscat" className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Address</label>
                <input type="text" value={settings.bank_details?.address || ""} onChange={(e) => handleBankDetailChange("address", e.target.value)} placeholder="e.g. PC 115 MSQ 895, Muscat" className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Branch</label>
                <input type="text" value={settings.bank_details?.branch || ""} onChange={(e) => handleBankDetailChange("branch", e.target.value)} placeholder="e.g. Bausher" className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Beneficiary Name</label>
                <input type="text" value={settings.bank_details?.beneficiaryName || ""} onChange={(e) => handleBankDetailChange("beneficiaryName", e.target.value)} placeholder="Company account name" className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">IBAN</label>
                <input type="text" value={settings.bank_details?.iban || ""} onChange={(e) => handleBankDetailChange("iban", e.target.value)} placeholder="e.g. OM030270329027833640018" className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Account No</label>
                <input type="text" value={settings.bank_details?.accountNo || ""} onChange={(e) => handleBankDetailChange("accountNo", e.target.value)} placeholder="e.g. 0329-02783364-001-8" className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Swift Code</label>
                <input type="text" value={settings.bank_details?.swiftCode || ""} onChange={(e) => handleBankDetailChange("swiftCode", e.target.value)} placeholder="e.g. BMUSOMRXXXX" className="w-full border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600" />
              </div>
            </div>
          </div>
        )}

        {isFinanceOrAdmin && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <DollarSign size={20} className="text-cyan-600" />
              Default invoice pricing view
            </h2>
            <p className="form-helper mb-4 max-w-full">Choose how prices appear on new invoices by default. Sales can override per invoice when creating or editing.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Display mode</label>
                <select
                  value={defaultPricingView.mode || "sell_only"}
                  onChange={(e) => setDefaultPricingView((p) => ({ ...p, mode: e.target.value }))}
                  className="w-full md:w-80 border border-stone-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                >
                  {PRICING_VIEW_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              {defaultPricingView.mode === "margin" && (
                <label className="flex items-center gap-2 text-sm text-stone-700">
                  <input
                    type="checkbox"
                    checked={defaultPricingView.show_markup === true}
                    onChange={(e) => setDefaultPricingView((p) => ({ ...p, show_markup: e.target.checked }))}
                    className="rounded"
                  />
                  Show as markup % (instead of margin %)
                </label>
              )}
              <button
                type="button"
                onClick={async () => {
                  setSavingPricingView(true);
                  setMessage({ type: "", text: "" });
                  try {
                    await api.settings.updateDefaultPricingView(defaultPricingView);
                    setMessage({ type: "success", text: "Default pricing view saved." });
                  } catch (err) {
                    setMessage({ type: "error", text: err.message || "Failed to save" });
                  } finally {
                    setSavingPricingView(false);
                  }
                }}
                disabled={savingPricingView}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm disabled:opacity-50"
              >
                {savingPricingView ? "Saving..." : "Save default pricing view"}
              </button>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <Shield size={20} className="text-cyan-600" />
              Minimum sale prices (per service type)
            </h2>
            <p className="form-helper mb-4 max-w-full">Set a minimum sale amount per service type. When closing an order, any line below its minimum will block invoice creation.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-stone-600">Service type</th>
                    <th className="text-left py-2 px-3 font-medium text-stone-600">Min. amount</th>
                  </tr>
                </thead>
                <tbody>
                  {serviceTypes.map((st) => (
                    <tr key={st.id} className="border-b border-stone-100">
                      <td className="py-2 px-3">{st.label}</td>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={minPrices[st.id] ?? ""}
                          onChange={(e) => handleMinPriceChange(st.id, e.target.value)}
                          placeholder="No minimum"
                          className="w-32 border border-stone-300 rounded px-2 py-1"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={handleSaveMinPrices}
              disabled={savingMinPrices}
              className="mt-4 px-4 py-2 bg-stone-700 hover:bg-stone-800 text-white rounded-lg text-sm disabled:opacity-50"
            >
              {savingMinPrices ? "Saving..." : "Save minimum prices"}
            </button>
          </div>
        )}

        {isAdmin && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <FileText size={20} className="text-cyan-600" />
              Invoice number format
            </h2>
            <p className="form-helper mb-4 max-w-full">
              All new invoices will use this format. Use <strong>Prefix</strong> (e.g. CAN), optional <strong>Year</strong>, and a <strong>random value</strong> (digits or characters). Optional <strong>Postfix</strong> can be added after the random part. Example: CAN/2026/1234 or INV-2027-A7K9.
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Prefix</label>
                  <input
                    type="text"
                    value={invoiceNumberFormat.prefix || ""}
                    onChange={(e) => setInvoiceNumberFormat((p) => ({ ...p, prefix: e.target.value }))}
                    placeholder="CAN"
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  />
                  <p className="form-helper mt-1">Text before year/random (e.g. CAN, INV)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Postfix (optional)</label>
                  <input
                    type="text"
                    value={invoiceNumberFormat.postfix || ""}
                    onChange={(e) => setInvoiceNumberFormat((p) => ({ ...p, postfix: e.target.value }))}
                    placeholder=""
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  />
                  <p className="form-helper mt-1">Text after the random part, if any</p>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-stone-700">
                <input
                  type="checkbox"
                  checked={invoiceNumberFormat.use_year !== false}
                  onChange={(e) => setInvoiceNumberFormat((p) => ({ ...p, use_year: e.target.checked }))}
                  className="rounded"
                />
                Include current year in number (e.g. CAN/2026/1234)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Random part type</label>
                  <select
                    value={invoiceNumberFormat.random_type || "digits"}
                    onChange={(e) => setInvoiceNumberFormat((p) => ({ ...p, random_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  >
                    <option value="digits">Digits only (e.g. 1234)</option>
                    <option value="chars">Letters and numbers (e.g. A7K9)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Random length (1–10)</label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={invoiceNumberFormat.random_length ?? 4}
                    onChange={(e) => setInvoiceNumberFormat((p) => ({ ...p, random_length: parseInt(e.target.value, 10) || 4 }))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-stone-500">
                Preview: {invoiceNumberFormat.prefix || "CAN"}{invoiceNumberFormat.use_year !== false ? `/${new Date().getFullYear()}` : ""}/{invoiceNumberFormat.random_type === "chars" ? "XXXX" : "1234"}{invoiceNumberFormat.postfix ? `/${invoiceNumberFormat.postfix}` : ""}
              </p>
              <button
                type="button"
                onClick={async () => {
                  setSavingInvoiceNumberFormat(true);
                  setMessage({ type: "", text: "" });
                  try {
                    await api.settings.updateInvoiceNumberFormat(invoiceNumberFormat);
                    setMessage({ type: "success", text: "Invoice number format saved. New invoices will use this format." });
                  } catch (err) {
                    setMessage({ type: "error", text: err.message || "Failed to save" });
                  } finally {
                    setSavingInvoiceNumberFormat(false);
                  }
                }}
                disabled={savingInvoiceNumberFormat}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm disabled:opacity-50"
              >
                {savingInvoiceNumberFormat ? "Saving..." : "Save invoice number format"}
              </button>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <FileText size={20} className="text-cyan-600" />
              Invoice template
            </h2>
            <p className="form-helper mb-4 max-w-full">Customize how invoice PDFs and the invoice table look. Primary colour, table header, logo, and footer.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Primary colour (headings, accents)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={invoiceTemplate.primary_color || "#0891b2"}
                    onChange={(e) => setInvoiceTemplate((p) => ({ ...p, primary_color: e.target.value }))}
                    className="w-12 h-10 rounded border border-stone-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={invoiceTemplate.primary_color || "#0891b2"}
                    onChange={(e) => setInvoiceTemplate((p) => ({ ...p, primary_color: e.target.value }))}
                    className="flex-1 max-w-[120px] px-3 py-2 border border-stone-300 rounded-lg font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Table header background</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={invoiceTemplate.table_header_bg || "#e5e7eb"}
                    onChange={(e) => setInvoiceTemplate((p) => ({ ...p, table_header_bg: e.target.value }))}
                    className="w-12 h-10 rounded border border-stone-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={invoiceTemplate.table_header_bg || "#e5e7eb"}
                    onChange={(e) => setInvoiceTemplate((p) => ({ ...p, table_header_bg: e.target.value }))}
                    className="flex-1 max-w-[120px] px-3 py-2 border border-stone-300 rounded-lg font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Table header text</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={invoiceTemplate.table_header_text || invoiceTemplate.primary_color || "#0891b2"}
                    onChange={(e) => setInvoiceTemplate((p) => ({ ...p, table_header_text: e.target.value }))}
                    className="w-12 h-10 rounded border border-stone-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={invoiceTemplate.table_header_text || invoiceTemplate.primary_color || "#0891b2"}
                    onChange={(e) => setInvoiceTemplate((p) => ({ ...p, table_header_text: e.target.value }))}
                    className="flex-1 max-w-[120px] px-3 py-2 border border-stone-300 rounded-lg font-mono text-sm"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-stone-700">
                <input
                  type="checkbox"
                  checked={invoiceTemplate.show_logo !== false}
                  onChange={(e) => setInvoiceTemplate((p) => ({ ...p, show_logo: e.target.checked }))}
                  className="rounded"
                />
                Show company logo on invoice PDF
              </label>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Footer text (optional)</label>
                <textarea
                  value={invoiceTemplate.footer_text || ""}
                  onChange={(e) => setInvoiceTemplate((p) => ({ ...p, footer_text: e.target.value }))}
                  rows={2}
                  placeholder="e.g. Thank you for your business"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg resize-none text-sm"
                />
              </div>
              <button
                type="button"
                onClick={async () => {
                  setSavingTemplate(true);
                  setMessage({ type: "", text: "" });
                  try {
                    await api.settings.updateInvoiceTemplate(invoiceTemplate);
                    setMessage({ type: "success", text: "Invoice template saved." });
                  } catch (err) {
                    setMessage({ type: "error", text: err.message || "Failed to save template" });
                  } finally {
                    setSavingTemplate(false);
                  }
                }}
                disabled={savingTemplate}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm disabled:opacity-50"
              >
                {savingTemplate ? "Saving..." : "Save invoice template"}
              </button>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="submit"
            disabled={saving}
            className="bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg px-6 py-2.5 shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save size={18} />
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> These settings will be used on all invoices you
          create. Make sure your company details are accurate as they will
          appear on customer invoices.
        </p>
      </div>
    </div>
  );
}
